import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import { DatePicker, InputField, Select } from "components/fields";
import Loader from "components/loader/Loader";
import { Form, Formik } from "formik";
import { useAuth } from "hooks/useAuth";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import notify from "lib/notify";
import { useEffect, useRef, useState } from "react";
import { MdFileUpload } from "react-icons/md";
import * as Yup from "yup";
import SettingsSection, {
  SettingsFormActions,
  SettingsSectionSkeleton,
} from "./components/SettingsSection";
import roleLabel from "./components/roleLabel";

dayjs.extend(customParseFormat);

const FAMILY_RELATIONSHIP_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "married", label: "Married" },
  { value: "child", label: "Child" },
];

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
    .required("Phone number is required"),
  familyRelationship: Yup.string().required("Family relationship is required"),
  dob: Yup.date().nullable(),
});

const locationValidationSchema = Yup.object().shape({
  address: Yup.string().nullable(),
  city: Yup.string().nullable(),
  state: Yup.string().nullable(),
  country: Yup.string().nullable(),
  zip: Yup.string().nullable(),
});

// The API stores dob as YYYY-MM-DD; the picker edits MM/DD/YYYY.
const toPickerValue = (dob) =>
  dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)
    ? `${dob.slice(5, 7)}/${dob.slice(8, 10)}/${dob.slice(0, 4)}`
    : dob || "";

const fromPickerValue = (v) => {
  if (typeof v !== "string") return dayjs(v).format("YYYY-MM-DD");
  if (v.length === 10 && v.includes("/")) {
    const parsed = dayjs(v, "MM/DD/YYYY");
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  return v;
};

const ProfileDetails = () => {
  const auth = useAuth();
  const currentUser = auth.user || {};
  const [user] = useState({
    email: currentUser.email || "",
    phone: currentUser.phone || "",
    firstName: currentUser.firstName || "",
    lastName: currentUser.lastName || "",
    dob: currentUser.dob || "",
    address: currentUser.address || "",
    city: currentUser.city || "",
    state: currentUser.state || "",
    country: currentUser.country || "",
    zip: currentUser.zip || "",
    familyRelationship: currentUser.familyRelationship || "",
  });
  const fileInputRef = useRef(null);
  const [file, setFile] = useState();
  const [uploading, setUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  const getProfileDetails = async () => {
    AtriumApi.get("/v1/users/get-profile").then((resp) => {
      auth.setUser(resp?.data?.user);
    });
  };

  const handleFileChange = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setUploading(true);
    setFile(picked);

    const formData = new FormData();
    formData.append("avatar", picked);

    try {
      await AtriumApi.patch(`/v1/users/update/${currentUser?.id}`, formData, MULTIPART);
      notify.success("Profile photo saved");
      getProfileDetails();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not upload profile photo"));
    } finally {
      e.target.value = "";
      setUploading(false);
      setFile(undefined);
    }
  };

  const onSaveDetails = async (values) => {
    setIsSaving(true);

    let { email, phone, dob, firstName, lastName, familyRelationship } = values;
    if (familyRelationship === "") familyRelationship = null;
    const formData = new FormData();

    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("dob", dob);
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("familyRelationship", familyRelationship);

    try {
      const response = await AtriumApi.patch(
        `/v1/users/update/${currentUser?.id}`,
        formData,
        MULTIPART
      );

      if (response.status === 200) {
        getProfileDetails();
        notify.success("Personal details saved");
      } else {
        throw new Error("unexpected response");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save personal details"));
    } finally {
      setIsSaving(false);
    }
  };

  const onSaveLocation = async (values) => {
    setIsSavingLocation(true);

    const { address, city, state, country, zip } = values;
    const formData = new FormData();

    formData.append("address", address || "");
    formData.append("city", city || "");
    formData.append("state", state || "");
    formData.append("country", country || "");
    formData.append("zip", zip || "");

    try {
      const response = await AtriumApi.patch(
        `/v1/users/update/${currentUser?.id}`,
        formData,
        MULTIPART
      );

      if (response.status === 200) {
        getProfileDetails();
        notify.success("Location saved");
      } else {
        throw new Error("unexpected response");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save location"));
    } finally {
      setIsSavingLocation(false);
    }
  };

  useEffect(() => {
    getProfileDetails();
  }, []);

  if (!auth.user) return <SettingsSectionSkeleton />;

  const fullName = `${auth.user.firstName || ""} ${auth.user.lastName || ""}`.trim();

  return (
    <div className="flex flex-col gap-5">
      <SettingsSection
        title="Profile Photo"
        description="Your photo is saved as soon as you choose a file."
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            {file ? (
              <Avatar src={URL.createObjectURL(file)} name={fullName} size="xl" />
            ) : (
              <Avatar user={auth.user} size="xl" />
            )}
            {uploading && <Loader variant="overlay" size="sm" label="Uploading photo" className="rounded-full" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold text-fg">{fullName}</p>
            {auth.user.role && (
              <Badge tone="neutral" className="mt-1">
                {roleLabel(auth.user.role)}
              </Badge>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={handleFileChange}
          />
          <Button
            variant="secondary"
            leftIcon={<MdFileUpload />}
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading…" : auth.user.avatar?.url ? "Change Photo" : "Upload Photo"}
          </Button>
        </div>
      </SettingsSection>

      <Formik
        enableReinitialize
        initialValues={user}
        validationSchema={validationSchema}
        onSubmit={onSaveDetails}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <SettingsSection title="Personal Details" description="Update your personal information.">
            <Form noValidate>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  label="Email Address"
                  required
                  placeholder="Enter your email address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  error={errors.email}
                  touched={touched.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="Phone Number"
                  required
                  placeholder="Enter your phone number"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={values.phone}
                  error={errors.phone}
                  touched={touched.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="First Name"
                  required
                  placeholder="Enter your first name"
                  name="firstName"
                  autoComplete="given-name"
                  value={values.firstName}
                  error={errors.firstName}
                  touched={touched.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="Last Name"
                  required
                  placeholder="Enter your last name"
                  name="lastName"
                  autoComplete="family-name"
                  value={values.lastName}
                  error={errors.lastName}
                  touched={touched.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <DatePicker
                  name="dob"
                  isFutureDateDisabled
                  label="Date of Birth"
                  value={toPickerValue(values.dob)}
                  onChange={(v) => setFieldValue("dob", fromPickerValue(v))}
                  onBlur={handleBlur}
                />
                <Select
                  label="Family Relationship"
                  required
                  name="familyRelationship"
                  placeholder="Select your family relationship"
                  options={FAMILY_RELATIONSHIP_OPTIONS}
                  value={values.familyRelationship}
                  error={errors.familyRelationship}
                  touched={touched.familyRelationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <SettingsFormActions>
                <Button type="submit" loading={isSaving}>
                  {isSaving ? "Saving…" : "Save Changes"}
                </Button>
              </SettingsFormActions>
            </Form>
          </SettingsSection>
        )}
      </Formik>

      <Formik
        enableReinitialize
        initialValues={{
          address: currentUser.address || "",
          city: currentUser.city || "",
          state: currentUser.state || "",
          country: currentUser.country || "",
          zip: currentUser.zip || "",
        }}
        validationSchema={locationValidationSchema}
        onSubmit={onSaveLocation}
      >
        {({ values, handleChange, handleBlur }) => (
          <SettingsSection title="Location" description="Update your mailing address.">
            <Form noValidate>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  label="Address"
                  placeholder="Enter your address"
                  name="address"
                  autoComplete="street-address"
                  value={values.address || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="City"
                  placeholder="Enter your city"
                  name="city"
                  autoComplete="address-level2"
                  value={values.city || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="State"
                  placeholder="Enter your state"
                  name="state"
                  autoComplete="address-level1"
                  value={values.state || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <InputField
                  label="Zip Code"
                  placeholder="Enter your zip code"
                  name="zip"
                  autoComplete="postal-code"
                  value={values.zip || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <SettingsFormActions>
                <Button type="submit" loading={isSavingLocation}>
                  {isSavingLocation ? "Saving…" : "Save Changes"}
                </Button>
              </SettingsFormActions>
            </Form>
          </SettingsSection>
        )}
      </Formik>
    </div>
  );
};

export default ProfileDetails;
